'use client';
import React from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Sparkles, Lightbulb, Zap, Palette, Users } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

export default function AboutContent() {
  const { t } = useLanguage();
  const ap = t?.aboutPage;

  const pillars = [
    {
      icon: Lightbulb,
      title: ap?.pillar1Title ?? 'Creative Planning',
      desc: ap?.pillar1Desc ?? 'Turn raw ideas into structured creative plans, scripts, and content strategies.',
    },
    {
      icon: Palette,
      title: ap?.pillar2Title ?? 'Image & SVG Generation',
      desc: ap?.pillar2Desc ?? 'Generate stunning visuals, SVG files, coloring pages, logos, and print-on-demand graphics.',
    },
    {
      icon: Zap,
      title: ap?.pillar3Title ?? 'Storytelling Tools',
      desc: ap?.pillar3Desc ?? "Build children's books, scripts, narratives, and story-driven content with AI assistance.",
    },
    {
      icon: Users,
      title: ap?.pillar4Title ?? 'Marketing Assistance',
      desc: ap?.pillar4Desc ?? 'Create high-converting ad copy, social captions, product descriptions, and campaign content.',
    },
  ];

  const audience = [
    ap?.audience1 ?? 'Creators & Content Makers',
    ap?.audience2 ?? 'Entrepreneurs',
    ap?.audience3 ?? 'Educators & Teachers',
    ap?.audience4 ?? 'Authors & Writers',
    ap?.audience5 ?? 'Artists & Designers',
    ap?.audience6 ?? 'Small Business Owners',
    ap?.audience7 ?? 'Etsy & Shopify Sellers',
    ap?.audience8 ?? 'Print-on-Demand Businesses',
    ap?.audience9 ?? 'Crafters & DIY Makers',
    ap?.audience10 ?? 'Social Media Managers',
  ];

  return (
    <main className="min-h-screen bg-background pt-24 pb-20">
      {/* Hero */}
      <section className="max-w-screen-xl mx-auto px-6 lg:px-10 mb-20">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 mb-6">
            <Sparkles size={13} className="text-primary" />
            <span className="text-xs font-600 text-primary">{ap?.badge ?? 'About AIMaker Studio'}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-800 text-foreground mb-6 leading-tight">
            {ap?.headline ?? 'AI-Powered Creativity,'}{' '}
            <span className="gradient-text">{ap?.headlineHighlight ?? 'Built for Everyone'}</span>
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed mb-4">
            {ap?.intro1 ?? 'AIMaker Studio is an AI-powered creative platform developed by'}{' '}
            <span className="text-foreground font-600">BeMel Productions LLC</span>.
          </p>
          <p className="text-base text-muted-foreground leading-relaxed">
            {ap?.intro2 ?? 'Our mission is to help creators, entrepreneurs, educators, authors, artists, and small businesses transform ideas into content, products, and opportunities using artificial intelligence.'}
          </p>
        </div>
      </section>
      {/* Mission */}
      <section className="max-w-screen-xl mx-auto px-6 lg:px-10 mb-20">
        <div className="glass-card rounded-2xl p-8 md:p-12 border border-border">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-2xl font-700 text-foreground mb-4">{ap?.missionTitle ?? 'Our Mission'}</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                {ap?.missionP1 ?? 'We believe AI should be'} <span className="text-foreground font-500">{ap?.missionP1Bold ?? 'accessible, practical, and empowering'}</span> {ap?.missionP1End ?? 'for people at every skill level — not just for large companies or technical experts.'}
              </p>
              <p className="text-muted-foreground leading-relaxed">
                {ap?.missionP2 ?? 'AIMaker Studio combines creative planning, image generation, SVG creation, storytelling tools, marketing assistance, and future AI-powered creative workflows in one platform — so you can focus on your ideas, not the tools.'}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {pillars?.map((p, i) => (
                <div key={`pillar-${i}`} className="bg-muted/50 rounded-xl p-4 border border-border/50">
                  <p.icon size={20} className="text-primary mb-2" />
                  <p className="text-sm font-600 text-foreground mb-1">{p?.title}</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">{p?.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      {/* Who We Serve */}
      <section className="max-w-screen-xl mx-auto px-6 lg:px-10 mb-20">
        <h2 className="text-2xl font-700 text-foreground mb-2">{ap?.whoWeServeTitle ?? 'Who We Serve'}</h2>
        <p className="text-muted-foreground mb-8">{ap?.whoWeServeSubtitle ?? 'AIMaker Studio is built for a wide range of creators and professionals.'}</p>
        <div className="flex flex-wrap gap-3">
          {audience?.map((label) => (
            <span
              key={`audience-${label}`}
              className="px-4 py-2 rounded-full bg-muted border border-border text-sm text-muted-foreground hover:text-foreground hover:border-primary/30 transition-colors"
            >
              {label}
            </span>
          ))}
        </div>
      </section>
      {/* Brand Statement */}
      <section className="max-w-screen-xl mx-auto px-6 lg:px-10 mb-20">
        <div className="glass-card rounded-2xl p-8 md:p-12 border border-border flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 mb-6">
              <AppLogo size={36} />
              <span className="font-800 text-xl text-foreground">AIMaker Studio</span>
            </div>
            <p className="text-muted-foreground leading-relaxed mb-4">
              {ap?.brandP1 ?? "AIMaker Studio is more than a tool — it's a creative partner for people who want to build, create, and grow using the power of artificial intelligence."}
            </p>
            <p className="text-muted-foreground leading-relaxed mb-6">
              {ap?.brandP2 ?? 'From generating your first image to building a full product line for your Etsy shop, AIMaker Studio is designed to grow with you at every stage of your creative journey.'}
            </p>
            <p className="text-sm text-muted-foreground">
              {ap?.brandFootnote ?? 'AIMaker Studio is a product of'} <span className="text-foreground font-500">BeMel Productions LLC</span>, {ap?.brandFootnoteLocation ?? 'Georgia, United States.'}
            </p>
          </div>
          <div className="mt-8 pt-6 border-t border-border flex flex-col sm:flex-row gap-3">
            <Link
              href="/demo"
              className="btn-primary px-5 py-2.5 rounded-xl text-sm flex items-center justify-center gap-2"
            >
              <Sparkles size={14} />
              {ap?.ctaStart ?? 'Try Demo'}
            </Link>
            <Link
              href="/contact"
              className="btn-secondary px-5 py-2.5 rounded-xl text-sm flex items-center justify-center"
            >
              {ap?.ctaContact ?? 'Contact Us'}
            </Link>
          </div>
        </div>
      </section>
      {/* Footer note */}
      <section className="max-w-screen-xl mx-auto px-6 lg:px-10">
        <div className="text-center py-8 border-t border-border">
          <p className="text-xs text-muted-foreground">
            {ap?.copyright ?? '© 2026'} <span className="text-foreground/70 font-500">BeMel Productions LLC</span>. {ap?.allRights ?? 'All Rights Reserved.'} ·{' '}
            <Link href="/privacy-policy" className="hover:text-foreground transition-colors">{t?.legal?.privacy ?? 'Privacy Policy'}</Link>
            {' · '}
            <Link href="/terms-of-service" className="hover:text-foreground transition-colors">{t?.legal?.terms ?? 'Terms of Service'}</Link>
          </p>
        </div>
      </section>
    </main>
  );
}
